import { cva } from "class-variance-authority";
import { cn } from "../../../client/cn";
import { buttonColors, type ColorVariant } from "../../../shared/colors";
import {
  BUTTON_PRIMITIVE_DEFAULTS,
  BUTTON_SIZE_CLASSES,
  CANONICAL_BUTTON_BASE_CLASSES,
  CANONICAL_BUTTON_TONES,
  LEGACY_COLOR_TO_CANONICAL_TONE,
  isLegacyColorVariant,
  type CanonicalButtonSize,
  type CanonicalButtonTone,
  type CanonicalNonIconVariant,
} from "./buttonPrimitiveSpec";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  tone?: CanonicalButtonTone;
  size?: CanonicalButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
}

export type ButtonVariant = ColorVariant | CanonicalNonIconVariant;

const solidToneClasses: Record<CanonicalButtonTone, string> =
  CANONICAL_BUTTON_TONES.reduce(
    (acc, tone) => {
      acc[tone] =
        `${buttonColors[tone].base} ${buttonColors[tone].hover} ${buttonColors[tone].focus} ${buttonColors[tone].disabled}`;
      return acc;
    },
    {} as Record<CanonicalButtonTone, string>,
  );

const outlineToneClasses: Record<CanonicalButtonTone, string> = {
  primary:
    "border border-primary text-primary hover:bg-primary/10 focus-visible:ring-primary/30",
  secondary:
    "border border-secondary text-secondary hover:bg-secondary/10 focus-visible:ring-secondary/30",
  success:
    "border border-success text-success hover:bg-success/10 focus-visible:ring-success/30",
  danger:
    "border border-danger text-danger hover:bg-danger/10 focus-visible:ring-danger/30",
  warning:
    "border border-warning text-warning hover:bg-warning/10 focus-visible:ring-warning/30",
  info: "border border-info text-info hover:bg-info/10 focus-visible:ring-info/30",
  neutral:
    "border border-stroke text-neutral-700 hover:bg-neutral-100 focus-visible:ring-neutral/30 dark:border-strokedark dark:text-neutral-200 dark:hover:bg-neutral-800",
};

const ghostToneClasses: Record<CanonicalButtonTone, string> = {
  primary: "text-primary hover:bg-primary/10 focus-visible:ring-primary/30",
  secondary:
    "text-secondary hover:bg-secondary/10 focus-visible:ring-secondary/30",
  success: "text-success hover:bg-success/10 focus-visible:ring-success/30",
  danger: "text-danger hover:bg-danger/10 focus-visible:ring-danger/30",
  warning: "text-warning hover:bg-warning/10 focus-visible:ring-warning/30",
  info: "text-info hover:bg-info/10 focus-visible:ring-info/30",
  neutral:
    "text-neutral-700 hover:bg-neutral-100 focus-visible:ring-neutral/30 dark:text-neutral-200 dark:hover:bg-neutral-800",
};

const linkToneClasses: Record<CanonicalButtonTone, string> = {
  primary:
    "rounded-none p-0 text-primary hover:text-primary/80 focus-visible:ring-primary/30",
  secondary:
    "rounded-none p-0 text-secondary hover:text-secondary/80 focus-visible:ring-secondary/30",
  success:
    "rounded-none p-0 text-success hover:text-success/80 focus-visible:ring-success/30",
  danger:
    "rounded-none p-0 text-danger hover:text-danger/80 focus-visible:ring-danger/30",
  warning:
    "rounded-none p-0 text-warning hover:text-warning/80 focus-visible:ring-warning/30",
  info: "rounded-none p-0 text-info hover:text-info/80 focus-visible:ring-info/30",
  neutral:
    "rounded-none p-0 text-neutral-700 hover:text-neutral-900 focus-visible:ring-neutral/30 dark:text-neutral-200 dark:hover:text-white",
};

const buttonVariants = cva(CANONICAL_BUTTON_BASE_CLASSES, {
  variants: {
    variant: {
      solid: "",
      outline: "",
      ghost: "",
      link: "",
      primary: solidToneClasses.primary,
      secondary: solidToneClasses.secondary,
      success: solidToneClasses.success,
      danger: solidToneClasses.danger,
      warning: solidToneClasses.warning,
      info: solidToneClasses.info,
      neutral: solidToneClasses.neutral,
    },
    tone: {
      primary: "",
      secondary: "",
      success: "",
      danger: "",
      warning: "",
      info: "",
      neutral: "",
    },
    size: BUTTON_SIZE_CLASSES,
    fullWidth: {
      true: "w-full",
      false: "",
    },
    loading: {
      true: "disabled:cursor-wait",
      false: "",
    },
  },
  compoundVariants: [
    ...CANONICAL_BUTTON_TONES.map((tone) => ({
      variant: "solid" as const,
      tone,
      className: solidToneClasses[tone],
    })),
    ...CANONICAL_BUTTON_TONES.map((tone) => ({
      variant: "outline" as const,
      tone,
      className: outlineToneClasses[tone],
    })),
    ...CANONICAL_BUTTON_TONES.map((tone) => ({
      variant: "ghost" as const,
      tone,
      className: ghostToneClasses[tone],
    })),
    ...CANONICAL_BUTTON_TONES.map((tone) => ({
      variant: "link" as const,
      tone,
      className: linkToneClasses[tone],
    })),
  ],
  defaultVariants: {
    variant: "solid",
    tone: BUTTON_PRIMITIVE_DEFAULTS.tone,
    size: BUTTON_PRIMITIVE_DEFAULTS.size,
    fullWidth: BUTTON_PRIMITIVE_DEFAULTS.fullWidth,
    loading: BUTTON_PRIMITIVE_DEFAULTS.loading,
  },
});

function resolveTone(
  variant: ButtonVariant,
  tone: CanonicalButtonTone | undefined,
): CanonicalButtonTone {
  return (
    tone ??
    (isLegacyColorVariant(variant)
      ? LEGACY_COLOR_TO_CANONICAL_TONE[variant]
      : BUTTON_PRIMITIVE_DEFAULTS.tone)
  );
}

export interface ButtonPrimitiveClassNameOptions {
  variant?: ButtonVariant;
  tone?: CanonicalButtonTone;
  size?: CanonicalButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  className?: string;
}

export function getButtonPrimitiveClassName({
  variant,
  tone,
  size,
  fullWidth = BUTTON_PRIMITIVE_DEFAULTS.fullWidth,
  loading = BUTTON_PRIMITIVE_DEFAULTS.loading,
  className = "",
}: ButtonPrimitiveClassNameOptions): string {
  const resolvedVariant = variant ?? "solid";
  const resolvedTone = resolveTone(resolvedVariant, tone);
  const resolvedSize = size ?? BUTTON_PRIMITIVE_DEFAULTS.size;

  return cn(
    buttonVariants({
      variant: resolvedVariant,
      tone: resolvedTone,
      size: resolvedSize,
      fullWidth,
      loading,
    }),
    className,
  );
}

export function Button({
  variant,
  tone,
  size,
  loading = BUTTON_PRIMITIVE_DEFAULTS.loading,
  fullWidth = BUTTON_PRIMITIVE_DEFAULTS.fullWidth,
  className = "",
  children,
  disabled,
  ...props
}: ButtonProps) {
  const isDisabled = Boolean(disabled || loading);
  const buttonClasses = getButtonPrimitiveClassName({
    variant,
    tone,
    size,
    fullWidth,
    loading,
    className,
  });

  return (
    <button
      {...props}
      className={buttonClasses}
      disabled={isDisabled}
      aria-busy={loading || undefined}
    >
      {children}
    </button>
  );
}
